import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import cn from 'classnames';

import { abeStylists } from 'actions/stylist/beSalonStylists.action';
import filterActiveStylists from 'bl/data/filterActiveStylists.bl';
import Button from 'components/form/Button.component';
import TypedOption from 'components/form/TypedOption.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';

import CN from './Filter.module.scss';

const L = logger('StaffFilter');

const StaffFilter = ({ onFilter, filtered, abeStylists }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const {
    value: activeStylists,
    loading: isLoadingStylists,
    error: errorStylists,
  } = useAsync(async () => {
    const result = await abeStylists();
    return filterActiveStylists(result?.is?.ok ? result?.json : []);
  }, [abeStylists]);

  const [selected, setSelected] = useState(asArray(filtered));
  const { val: isRendered, tip: toggle, nay: close } = useTip(false);

  const staffCount = selected.length;
  const hasFilteredStaff = staffCount > 0;

  useEffect(() => {
    setSelected(filtered);
  }, [filtered]);

  const handleToggleStaff = useCallback(
    (id) => {
      const newSelected = selected.includes(id) ? selected.filter((sId) => sId !== id) : [...selected, id];
      setSelected(newSelected);
    },
    [selected],
  );

  const handleApply = useCallback(() => {
    call('StaffFilter.onFilter', onFilter, selected);
    close();
  }, [close, onFilter, selected]);

  const handleToggleAll = useCallback(() => {
    const allIds = activeStylists.map((s) => s.id);
    const allStaff = allIds?.length && selected.length === allIds.length ? [] : allIds;

    setSelected(allStaff);
  }, [activeStylists, selected.length]);

  L.debug(
    '() selected sty ID:',
    selected,
    'All act sty:',
    activeStylists,
    'selected sty count',
    selected.length,
    isLoadingStylists,
    errorStylists,
  );

  const allStylistsSelected = selected?.length === activeStylists?.length;

  return (
    <div
      data-bem="StaffFilter"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div
        onClick={toggle}
        className={cn({
          [CN.filterButton]: true,
          [CN.isSelected]: hasFilteredStaff,
        })}
      >
        {selected.length === activeStylists?.length
          ? t('clientsEmailPage.staffFilter.all')
          : hasFilteredStaff && `${staffCount}`}
        <Space>{t('clientsEmailPage.staffFilter.placeholder')}</Space>
        <Icon variant="arrowTriangle" width="xs" />
      </div>

      {isRendered && (
        <DropdownFoldout width="lg" position="bottom" onClickOutside={close}>
          <DropdownFoldout.Header>
            <div className={CN.dropdownTitle}>{t('clientsEmailPage.staffFilter.title')}</div>
          </DropdownFoldout.Header>

          <DropdownFoldout.Body>
            <div className={CN.dropdownContent}>
              {activeStylists?.length > 0 && (
                <div className={CN.dropdownContent}>
                  {activeStylists?.map((sty) => (
                    <div key={sty.id} className={CN.staffItem}>
                      <div className={CN.staffName}>
                        {sty.userName}
                        <TypedOption
                          tag="StaffFilter__select--service"
                          type="simple-checkbox"
                          checked={selected.includes(sty.id)}
                          onCheck={() => handleToggleStaff(sty.id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DropdownFoldout.Body>

          <DropdownFoldout.Footer>
            <div className={CN.dropdownFooterContainer}>
              <Button variant="outline" onClick={handleToggleAll} tag="StaffFilter__clear--all">
                {allStylistsSelected
                  ? t('common.buttons.clearAll').toUpperCase()
                  : t('common.buttons.selectAll').toUpperCase()}
              </Button>
              <Button variant="primary" onClick={handleApply} tag="StaffFilter__done">
                {t('common.buttons.apply').toUpperCase()}
              </Button>
            </div>
          </DropdownFoldout.Footer>
        </DropdownFoldout>
      )}
    </div>
  );
};

const StaffFilterHoc = connect(
  (state) => ({
    //
  }),
  mapActionsToDispatchToProps({
    abeStylists,
  }),
)(StaffFilter);

export default StaffFilterHoc;
